// @oasis-permission-public: every signed-in reviewer can explain why a CapCode finding is unhelpful
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { SuspenseErrorComponent } from "~/components/error-states";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { isNotFoundError } from "~/lib/network/errors";
import {
  getCapCodeReviewFeedbackTargetQuery,
  submitCapCodeReviewFeedbackMutation,
} from "~/modules/capcode-review/feedback";
import type { ReviewFeedbackTarget } from "~/modules/capcode-review/schemas";

export const Route = createFileRoute("/_dash/review-feedback/$feedbackId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      getCapCodeReviewFeedbackTargetQuery.queryOptions({
        pathParams: { feedbackId: params.feedbackId },
        params: {},
      })
    ),
  component: RouteComponent,
  errorComponent: FeedbackError,
});

function FeedbackError({ error }: { error: Error }) {
  return isNotFoundError(error) ? (
    <FeedbackUnavailable />
  ) : (
    <SuspenseErrorComponent error={error} />
  );
}

function FeedbackUnavailable() {
  return (
    <main className="mx-auto w-full max-w-2xl space-y-2 px-4 py-8">
      <h1 className="text-2xl font-semibold">Feedback link unavailable</h1>
      <p className="text-sm text-muted-foreground">
        This link is invalid or its CapCode finding is no longer available.
      </p>
    </main>
  );
}

function RouteComponent() {
  const { feedbackId } = Route.useParams();
  const { data: target } = getCapCodeReviewFeedbackTargetQuery.useSuspenseQuery({
    pathParams: { feedbackId },
    params: {},
  });

  return <ReviewFeedbackForm key={feedbackId} target={target} />;
}

function ReviewFeedbackForm({ target }: { target: ReviewFeedbackTarget }) {
  const formRef = useRef<HTMLFormElement>(null);
  const [feedback, setFeedback] = useState(target.existingFeedback?.feedback ?? "");
  const [submitted, setSubmitted] = useState(false);
  const { mutate, isPending } = submitCapCodeReviewFeedbackMutation.useMutation({
    onSuccess() {
      setSubmitted(true);
      toast.success("Feedback submitted");
    },
    onError() {
      toast.error("Could not submit feedback. Please try again.");
    },
  });

  const trimmedFeedback = feedback.trim();
  let submitButtonLabel = "Submit feedback";
  if (target.existingFeedback || submitted) {
    submitButtonLabel = "Update feedback";
  }
  if (isPending) {
    submitButtonLabel = "Submitting…";
  }

  return (
    <main className="mx-auto w-full max-w-2xl space-y-6 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">CapCode finding feedback</h1>
        <p className="text-sm text-muted-foreground">
          Tell us what is wrong or not useful about this finding. This will not change the GitHub
          review.
        </p>
      </div>

      <section className="space-y-3 rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium">
            {target.repo}#{target.pr} · {target.path}:{target.line}
          </span>
          <a
            href={target.githubCommentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-4 hover:underline"
          >
            View GitHub comment ↗
          </a>
        </div>
        {target.findingTitle ? <h2 className="font-medium">{target.findingTitle}</h2> : null}
        <p className="whitespace-pre-wrap text-sm leading-6">{target.findingBody}</p>
      </section>

      <form
        ref={formRef}
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!trimmedFeedback || isPending) {
            return;
          }
          mutate({
            pathParams: { feedbackId: target.feedbackId },
            params: { feedback: trimmedFeedback },
          });
        }}
      >
        <label htmlFor="review-feedback" className="block text-sm font-medium">
          What is wrong or not useful about this finding?
        </label>
        <Textarea
          id="review-feedback"
          autoFocus
          className="min-h-32"
          maxLength={2000}
          disabled={isPending}
          placeholder="For example: this path is unreachable because the caller already guards it."
          value={feedback}
          onChange={(event) => {
            setFeedback(event.target.value);
            setSubmitted(false);
          }}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              event.preventDefault();
              formRef.current?.requestSubmit();
            }
          }}
        />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!trimmedFeedback || isPending}>
            {submitButtonLabel}
          </Button>
          <span className="text-xs text-muted-foreground">⌘/Ctrl + Enter</span>
          {submitted ? <span className="text-sm text-green-600">Submitted</span> : null}
        </div>
      </form>
    </main>
  );
}
